import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDomainConfig } from '../../../../lib/domainConfig';
import { expectUnreachable } from '../../../../lib/util';
import {
  BandSizeDetails,
  f1BandSizeOptions,
  f3BandSizeOptions,
  sizeDetailsForSizeOption,
  MeasurementUnit,
  WeightUnit,
  getDefaultWeightUnit,
  lbsToKg,
  kgToLbs,
  weightLimits,
} from '../../../../lib/size';
import { BandSeries, SizeOption } from '../../../../types';
import SizingBlock from './SizingBlock';
import styles from './DynamicSizingGuide.module.scss';
import { type Breed, type BreedWeight } from './BreedDataset';
import DynamicSizingGuideResult from './DynamicSizingGuideResult';
import classNames from 'classnames';
import * as events from '../../../../lib/analytics/events';
import Loading from '../../../../components/Loading';
import { FiLanguage, useLocale } from '../../../../contexts/LocaleContext';

interface SizingGuideProps {
  bandSeries: BandSeries;
  onSelect: (newValue: SizeOption) => void;
  close?: () => void;
  measurementUnit?: MeasurementUnit;
}

interface SizingBlockProps {
  sizeOption: SizeOption;
  bandSizeDetails: BandSizeDetails;
  onSelect: (newValue: SizeOption) => void;
}

function SizingBlockForSizeOption({ sizeOption, bandSizeDetails, onSelect }: SizingBlockProps) {
  return (
    <SizingBlock
      key={sizeOption}
      dogList={bandSizeDetails.dogList}
      sizeName={bandSizeDetails.sizeName}
      neckSize={bandSizeDetails.neckSize}
      bandWidth={bandSizeDetails.bandWidth}
      onClick={() => onSelect(sizeOption)}
    />
  );
}

function getBreedName(breed: Breed, language: FiLanguage): string {
  return breed.locale[language] ?? '';
}

export async function getSizingGuideRecommendation(
  weight: number,
  breedName: string,
  isPuppy: boolean,
): Promise<BreedWeight> {
  const { Breeds, GenericAdultsSizingGuide, GenericPuppySizingGuide } = await import('./BreedDataset');
  const breed = Breeds.find((b) => b.locale[FiLanguage.EN_US] === breedName);
  const ceiledWeight = Math.ceil(weight / 10) * 10;

  if (breed?.sizing) {
    const weights = isPuppy ? breed.sizing.puppyWeights : breed.sizing.adultWeights;
    const match = weights.find((row) => row.weight === ceiledWeight);
    if (match) return match;
  }

  const fallbackDataset = isPuppy ? GenericPuppySizingGuide : GenericAdultsSizingGuide;
  const fallback = fallbackDataset[String(ceiledWeight)];
  if (fallback) return { weight: ceiledWeight, ...fallback };

  return { weight: ceiledWeight, XS: 0, S: 0, M: 100, L: 0, XL: 0 };
}

function WeightUnitPicker({ weightUnit, onChange }: { weightUnit: WeightUnit; onChange: (unit: WeightUnit) => void }) {
  return (
    <div className={styles.weightUnitPickerContainer}>
      <div
        className={classNames(styles.weightUnitPickerSlider, {
          [styles.sliderKg]: weightUnit === 'kg',
        })}
      />
      <button
        className={classNames(styles.weightUnitPickerOption, {
          [styles.active]: weightUnit === 'lbs',
        })}
        onClick={() => onChange('lbs')}
        type="button"
      >
        {'lb'}
      </button>
      <button
        className={classNames(styles.weightUnitPickerOption, {
          [styles.active]: weightUnit === 'kg',
        })}
        onClick={() => onChange('kg')}
        type="button"
      >
        {'kg'}
      </button>
    </div>
  );
}

export default function DynamicSizingGuide({ bandSeries, onSelect, close, measurementUnit }: SizingGuideProps) {
  const { t } = useTranslation();
  const { country, language } = useLocale();
  const allSizeOptions = useMemo(() => {
    if (bandSeries === BandSeries.F1) {
      return f1BandSizeOptions.map((sizeOption) => ({
        sizeOption,
        bandSizeDetails: sizeDetailsForSizeOption({ bandSeries, sizeOption }),
      }));
    } else if (bandSeries === BandSeries.F3) {
      return f3BandSizeOptions.map((sizeOption) => ({
        sizeOption,
        bandSizeDetails: sizeDetailsForSizeOption({ bandSeries, sizeOption, measurementUnit }),
      }));
    } else {
      expectUnreachable(bandSeries);
      throw new Error(`Unsupported band series: ${bandSeries}`);
    }
  }, [bandSeries, measurementUnit]);

  const sizeOptionList = useMemo(() => {
    return allSizeOptions.map(
      ({ bandSizeDetails, sizeOption }) =>
        bandSizeDetails && (
          <SizingBlockForSizeOption
            key={sizeOption}
            sizeOption={sizeOption}
            bandSizeDetails={bandSizeDetails}
            onSelect={onSelect}
          />
        ),
    );
  }, [allSizeOptions, onSelect]);

  const [currentBreed, setCurrentBreed] = useState<Breed | undefined>(undefined);
  const [showBreedSelector, setShowBreedSelector] = useState<boolean>(false);
  const [currentBreedSearch, setCurrentBreedSearch] = useState<string | undefined>(undefined);
  const [weightUnit, setWeightUnit] = useState<WeightUnit>(() => getDefaultWeightUnit(country));
  const [currentWeight, setCurrentWeight] = useState<number>(() =>
    getDefaultWeightUnit(country) === 'kg' ? lbsToKg(30) : 30,
  );
  const [userHasModifiedWeight, setUserHasModifiedWeight] = useState<boolean>(false);
  const range = weightLimits(weightUnit);

  const [dataset, setDataset] = useState<Pick<
    typeof import('./BreedDataset'),
    'Breeds' | 'GenericAdultsSizingGuide' | 'GenericPuppySizingGuide'
  > | null>(null);

  useEffect(() => {
    (async () => {
      const { Breeds, GenericAdultsSizingGuide, GenericPuppySizingGuide } = await import('./BreedDataset');
      setDataset({ Breeds, GenericAdultsSizingGuide, GenericPuppySizingGuide });
    })();
  }, []);

  if (!dataset) {
    return <Loading />;
  }

  const updateBreed = (breed: Breed) => {
    setCurrentBreed(breed);
    events.series3CollarSizingGuideBreedSelected(getBreedName(breed, language));
    if (!userHasModifiedWeight && breed.sizing) {
      const avgLbs = Math.round(breed.sizing.averageWeight);
      setCurrentWeight(weightUnit === 'kg' ? lbsToKg(avgLbs) : avgLbs);
    }
  };
  const updateWeight = (newWeight: number) => {
    events.series3CollarSizingGuideWeightUpdate(currentWeight, newWeight);
    setCurrentWeight(newWeight);
    setUserHasModifiedWeight(true);
  };
  const onWeightUnitChange = (newUnit: WeightUnit) => {
    if (newUnit === weightUnit) return;
    const converted = newUnit === 'kg' ? lbsToKg(currentWeight) : kgToLbs(currentWeight);
    setCurrentWeight(converted);
    setWeightUnit(newUnit);
  };
  const showSizingErrorMessage = currentWeight > range.max || currentWeight < range.min;

  const weightInLbs = weightUnit === 'kg' ? kgToLbs(currentWeight) : currentWeight;

  let sizingGuideResult: BreedWeight | undefined = undefined;
  let usePuppy = false;
  if (currentBreed?.sizing && currentWeight && currentWeight >= range.min && currentWeight <= range.max) {
    const weight = weightInLbs;
    const { averageWeight, weightStdDev } = currentBreed.sizing;

    usePuppy = weight <= averageWeight - weightStdDev;
    const weights = usePuppy ? currentBreed.sizing.puppyWeights : currentBreed.sizing.adultWeights;
    const ceiledWeight = Math.ceil(weight / 10) * 10;
    sizingGuideResult = weights.find((row) => row.weight === ceiledWeight);

    if (!sizingGuideResult) {
      const fallbackDataset = usePuppy ? dataset.GenericPuppySizingGuide : dataset.GenericAdultsSizingGuide;
      const fallback = fallbackDataset[String(ceiledWeight)];
      if (fallback) {
        sizingGuideResult = { weight: ceiledWeight, ...fallback };
      }
    }
  }

  return (
    <>
      <div className={classNames(styles.dynamicSizingMain, showBreedSelector ? styles.dynamicSizingNoScroll : null)}>
        <div className={styles.dynamicSizingSection}>
          <div className={styles.dynamicSizingDetailsSection}>
            <div className={styles.header}>{t('collar_sizing')}</div>
            <div className={styles.subHeader}>{t('need_help_tell_us_more_about_your_dog')}</div>
            <button
              className={styles.breedButton}
              onClick={() => {
                setShowBreedSelector(true);
                events.series3CollarSizingGuideBreedSelectorShown();
                setCurrentBreedSearch(undefined);
              }}
            >
              {!currentBreed && <div className={styles.breedPlaceholder}>{t('breed')}</div>}
              {currentBreed && <div className={styles.currentBreed}>{getBreedName(currentBreed, language)}</div>}
              <div className={styles.buttonArrow} />
            </button>
            <button
              className={classNames(styles.sizingButton, showSizingErrorMessage ? styles.sizingButtonError : null)}
            >
              <button
                className={styles.minusButton}
                onClick={() => {
                  if (currentWeight - 1 < range.min) return;
                  updateWeight(currentWeight - 1);
                }}
              >
                <div className={styles.minusButtonIcon} />
              </button>
              <div className={styles.weight}>
                <input
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={currentWeight}
                  min={range.min}
                  max={range.max}
                  step="1"
                  onChange={(e) => updateWeight(parseInt(e.currentTarget.value))}
                ></input>
                &nbsp;
                <div className={styles.poundsText}>
                  {weightUnit === 'lbs'
                    ? t('pounds_abbreviation', { count: currentWeight })
                    : t('kilograms_abbreviation', { count: currentWeight })}
                </div>
              </div>
              <button
                className={styles.plusButton}
                onClick={() => {
                  if (currentWeight + 1 > range.max) return;
                  updateWeight(currentWeight + 1);
                }}
              >
                <div className={styles.plusButtonIcon} />
              </button>
            </button>
            <WeightUnitPicker weightUnit={weightUnit} onChange={onWeightUnitChange} />
            {showSizingErrorMessage && (
              <div className={styles.sizingErrorMessage}>
                {weightUnit === 'lbs' ? t('weight_must_be_between_5_200_lbs') : t('weight_must_be_between_2_91_kg')}
              </div>
            )}
          </div>
          <div className={styles.dynamicSizingResultSection}>
            {sizingGuideResult && (
              <DynamicSizingGuideResult
                sizingGuideItem={sizingGuideResult}
                breedName={currentBreed ? getBreedName(currentBreed, language) : undefined}
                isPuppy={usePuppy}
                currentWeight={currentWeight}
                weightUnit={weightUnit}
                onSelect={(sizeOption: SizeOption) => {
                  events.series3CollarSizingGuideRecommendationSelected(
                    currentBreed ? getBreedName(currentBreed, language) : undefined,
                    currentWeight,
                    sizeOption,
                  );
                  onSelect(sizeOption);
                  close && close();
                }}
              />
            )}
          </div>
        </div>
        <div className={styles.sizeOptionSection}>
          <div className={styles.header}>{t('general_sizing_suggestions')}</div>
          <div className={styles.sizeOptionList}>
            {sizeOptionList}
            <div className={styles.stillUnsure}>
              <h3>{t('still_unsure')}</h3>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`https://${getDomainConfig().supportDomain}/hc/en-us/articles/360018860034-Sizing-and-Fit`}
              >
                {t('check_out_this_article')}
              </a>
              <p>
                {t(
                  'recommendations_based_on_average_breed_sizes_all_dogs_may_vary_measure_your_dogs_existing_collar_length_for_the_most_accurate_size_estimate',
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.breedSelectorContainer}>
        <div className={showBreedSelector ? styles.breedSelector : styles.breedSelectorHidden}>
          <button className={styles.backButton} onClick={() => setShowBreedSelector(false)}>
            <div className={styles.backButtonIcon} />
          </button>
          <div className={styles.header}>
            <div className={styles.headerText}>{t('select_breed')}</div>
          </div>
          <input
            className={styles.searchBarInput}
            value={currentBreedSearch || ''}
            onChange={(e) => setCurrentBreedSearch(e.currentTarget.value)}
            placeholder={t('search')}
          />
          <ul>
            {dataset.Breeds.filter((breed) => breed.sizing && breed.locale[language])
              .sort((a, b) => {
                const nameA = getBreedName(a, language);
                const nameB = getBreedName(b, language);
                if (nameA === 'Other') return 1;
                if (nameB === 'Other') return -1;
                return nameA.localeCompare(nameB);
              })
              .filter((breed) => {
                if (!currentBreedSearch) return true;
                const name = getBreedName(breed, language);
                return name.toLowerCase().includes(currentBreedSearch.toLowerCase());
              })
              .map((breed) => {
                const name = getBreedName(breed, language);
                return (
                  <li className={styles.breedListItem} key={name}>
                    <button
                      className={styles.breedListItemButton}
                      onClick={() => {
                        updateBreed(breed);
                        setShowBreedSelector(false);
                      }}
                    >
                      {name}
                    </button>
                  </li>
                );
              })}
          </ul>
        </div>
      </div>
    </>
  );
}
